'use client';
import { Button, Typography } from 'components';
import { CaretDoubleIcon } from 'lib/svgs/Icons';
import { useTranslations } from 'next-intl';
import { FC, RefObject, useState } from 'react';
import { timeShort } from 'utils/time/timeShort';
import { KeysRecentActivities, RecentActivityExtended } from '..';
import ActivityKey from './ActivityKey';
import { getInitTextHrefTitleAndEndTextFactory } from './factories';

interface IActivityLine {
  activity: RecentActivityExtended;
  activitiesLength?: number;
  index?: number;
  refFirstCard?: RefObject<HTMLDivElement>;
  refLastCard?: RefObject<HTMLDivElement>;
}

const ActivityLine: FC<IActivityLine> = ({ activity }) => {
  const t = useTranslations();

  const [expandActivities, setExpandActivities] = useState(false);

  const handleLink = (href: string) => () => {
    if (typeof window !== 'undefined') window.location.href = href;
  };

  const handleToggleActivities = () => setExpandActivities((oldState) => !oldState);

  const { href, init, title, text, status, end } = getInitTextHrefTitleAndEndTextFactory(
    activity,
    t
  );

  if (!Object.values(KeysRecentActivities).includes(activity.key as KeysRecentActivities))
    return null;

  return (
    <div key={activity.id} className="relative">
      {expandActivities && (
        <>
          {activity?.activities?.map((groupedActivity) => (
            <ActivityLine
              key={groupedActivity.key + groupedActivity.key}
              activity={groupedActivity}
            />
          ))}
        </>
      )}
      {!expandActivities && (
        <div className="flex gap-4 rounded-4xl pt-2">
          <div className="relative h-6 w-6">
            <ActivityKey activity={activity} status={status} />
          </div>
          <div className="flex w-full flex-col pl-1">
            <div>
              <Typography
                variant="light3"
                className="!mb-0 !mt-[3px] text-tertiary-100 dark:text-white"
              >
                {init}
              </Typography>
              <span>&nbsp;</span>
              {href === '#' ? (
                <Typography variant="medium3" className="!mt-[3px] mb-0 text-tertiary-100">
                  {title}
                </Typography>
              ) : (
                <Typography
                  variant="medium3"
                  className="!mt-[3px] mb-0 cursor-pointer text-secondary-link hover:opacity-80"
                  onClick={handleLink(href)}
                >
                  {title}
                </Typography>
              )}
              {end && <>&nbsp;</>}
              <Typography
                variant="light3"
                className="!mt-[3px] mb-0 text-tertiary-100 dark:text-white"
              >
                {end}
                {activity.key !== KeysRecentActivities.ThreadCreated && <span>.</span>}
              </Typography>
            </div>
            <Typography variant="light5" className="text-tertiary-200">
              {timeShort(activity.trackable?.updatedAt)}
            </Typography>
            {activity.key === KeysRecentActivities.CommentCreated && text && (
              <div className="mt-2 w-full rounded-xl bg-tertiary-800 p-3">
                <Typography
                  variant="light4"
                  className="!mt-[3px] mb-0 text-tertiary-200 dark:text-white"
                >
                  {text}
                </Typography>
              </div>
            )}
          </div>
        </div>
      )}
      {activity?.activities?.length && (
        <div className="relative my-6 mb-2 ml-10">
          <span className="absolute left-[-29px] top-[-54px] mt-4 h-[49px] border-l-[2px] border-tertiary-800" />
          <span className="absolute left-[-28px] top-[-7px] mt-4 w-7 border-b-[2px] border-tertiary-800" />
          <Button
            variant="text"
            noScale
            className="!m-0 !h-auto !justify-start !p-0"
            onClick={handleToggleActivities}
          >
            <span className="flex items-center ">
              <CaretDoubleIcon />
              <Typography variant="regular5" className="ml-1 text-secondary-link">
                {expandActivities
                  ? t('dashboard.recent_activities.expander.view_less')
                  : t('dashboard.recent_activities.expander.view_more', {
                      count: activity?.activities?.length - 1
                    })}
              </Typography>
            </span>
          </Button>
        </div>
      )}
    </div>
  );
};

export default ActivityLine;
